import { Box, Popover, Stack, Switch, Text, Tooltip, UnstyledButton } from '@mantine/core';
import { useDisclosure } from '@mantine/hooks';
import { IconCheck, IconExclamationMark, IconInfoCircle, IconX } from '@tabler/icons-react';
import moment from 'moment';
import React, { FC, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { pipe } from 'fp-ts/function';
import * as T from 'fp-ts/Task';
import * as TE from 'fp-ts/TaskEither';

import { getAvailableSlot, getAvailableTimesFromSlots } from '@/modules/booking/utils';
import API from '@/modules/common/api';
import { Api, BVScheduledFlight, SiteId } from '@/modules/common/model';
import { DateFormat, LocalDateTime } from '@/shared/modules/date';
import { useEffectEvent } from '@/shared/modules/react/hooks';

import { FormContent } from '../../components/Form';
import { Trip as TripModel, TripStatus } from '../../model';
import { FlightListing } from './FlightListing';
import { UnfoundTripNumberModal } from './UnfoundTripNumberModal';

const thumbColor: Record<TripStatus, string | undefined> = {
  [TripStatus.Unknown]: 'red',
  [TripStatus.Forced]: 'orange',
  [TripStatus.Found]: 'green',
  [TripStatus.None]: undefined,
  [TripStatus.Listed]: undefined,
};

const thumbIcon: Record<TripStatus, JSX.Element | undefined> = {
  [TripStatus.Unknown]: <IconX color={thumbColor[TripStatus.Unknown]} size="100%" />,
  [TripStatus.Forced]: <IconExclamationMark color={thumbColor[TripStatus.Forced]} size="100%" />,
  [TripStatus.Found]: <IconCheck color={thumbColor[TripStatus.Found]} size="100%" />,
  [TripStatus.None]: undefined,
  [TripStatus.Listed]: undefined,
};

interface Props {
  content: FormContent & { date: Date; hour: string };
  trip: TripModel;
  onChange: (content: FormContent) => void;
  arrivalSite: Api.Site;
  setEndTripStatus(status: TripStatus): void;
  slots: Api.SiteSlots | undefined;
  isFetchingSlots: boolean;
}

export const FlightSection: FC<Props> = ({
  content,
  trip,
  onChange,
  arrivalSite,
  setEndTripStatus,
  slots,
  isFetchingSlots,
}) => {
  const { t } = useTranslation();
  const [modalOpened, { open: openModal, close: closeModal }] = useDisclosure(false);
  const checked = useMemo(
    () => [TripStatus.Unknown, TripStatus.Forced, TripStatus.Found].includes(trip.status),
    [trip.status],
  );

  const [flights, setFlights] = useState<Array<BVScheduledFlight>>([]);
  const [loading, setLoading] = useState(false);

  const localDate = moment(content.date).format(DateFormat.LocalDate);
  const localDateTime = LocalDateTime.parse(`${localDate} ${content.hour}`);

  const flightsWithAvailability = useMemo(() => {
    if (!slots) return flights.map(flight => ({ ...flight, isUnavailable: false }));

    const availableSlots = getAvailableTimesFromSlots(slots);

    return flights.map(flight => {
      const matchingSlot = getAvailableSlot({ arrivalTime: flight.arrivalTime }, availableSlots);
      return { ...flight, isUnavailable: !matchingSlot || matchingSlot.isUnavailable };
    });
  }, [flights, slots]);

  const getFlightsArriving = useEffectEvent(
    (siteId: SiteId, signal: AbortSignal): TE.TaskEither<Error, Array<BVScheduledFlight>> =>
      pipe(
        TE.fromIO(() => () => setLoading(true)),
        TE.flatMap(() => API.getFlightsArriving(siteId, localDateTime, signal)),
        TE.chainFirstIOK(flights => () => setFlights(flights)),
        T.chainFirstIOK(() => () => setLoading(false)),
      ),
  );

  useEffect(() => {
    const controller = new AbortController();
    getFlightsArriving(arrivalSite.id, controller.signal)();
    return () => controller.abort();
  }, [arrivalSite.id, getFlightsArriving]);

  const handleFlightSelect = (flight: BVScheduledFlight) => {
    onChange({
      ...content,
      hour: flight.arrivalTime.split(' ')[1],
      tripNumber: flight.number,
      tripDetail: flight,
    });
    setEndTripStatus(TripStatus.Listed);
  };

  const handleConfirmWithTransport = (flight: BVScheduledFlight) => {
    onChange({
      ...content,
      hour: flight.arrivalTime.split(' ')[1],
      tripNumber: flight.number,
      tripDetail: flight,
    });
    setEndTripStatus(TripStatus.Found);
    closeModal();
  };

  const handleConfirmWithFlightNumber = (flightNumber: string) => {
    onChange({
      ...content,
      tripNumber: flightNumber,
      tripDetail: {},
    });
    setEndTripStatus(TripStatus.Forced);
    closeModal();
  };

  const handleConfirmNoFlightNumber = () => {
    const tripNumber = t('booking-trip_unknown_number_sel');

    setEndTripStatus(TripStatus.Unknown);

    onChange({ ...content, tripNumber, tripDetail: {} });

    closeModal();
  };

  const handleToggleUnknownTrip = () => {
    if ([TripStatus.Unknown, TripStatus.Forced, TripStatus.Found].includes(trip.status)) {
      setEndTripStatus(TripStatus.None);
      onChange({ ...content, tripNumber: undefined, tripDetail: {} });
    } else {
      openModal();
    }
  };

  const thumbLabel: Record<TripStatus, string> = {
    [TripStatus.Unknown]: t('booking-flight_status_unknown_label'),
    [TripStatus.Forced]: t('booking-flight_status_number_label', { tripNumber: content.tripNumber }),
    [TripStatus.Found]: t('booking-flight_status_number_label', { tripNumber: content.tripNumber }),
    [TripStatus.None]: t('booking-flight_switch_label'),
    [TripStatus.Listed]: t('booking-flight_switch_label'),
  };

  const statusTooltip: Record<TripStatus, string | null> = {
    [TripStatus.Unknown]: t('booking-flight_status_unknown_tooltip'),
    [TripStatus.Forced]: t('booking-flight_status_forced_tooltip'),
    [TripStatus.Found]: t('booking-flight_status_found_tooltip'),
    [TripStatus.None]: null,
    [TripStatus.Listed]: null,
  };

  return (
    <Stack pb="xl">
      <div className="FlightSectionTitle">
        <span className="FlightSectionTitle-label">{t('booking-flight_return_title')}</span>
        <Popover width={280} position="bottom-start" withArrow shadow="md" radius="md">
          <Popover.Target>
            <UnstyledButton
              type="button"
              className="FlightSectionTitle-info"
              aria-label={t('booking-flight_notice_title')}>
              <IconInfoCircle size={16} stroke={1.5} />
            </UnstyledButton>
          </Popover.Target>
          <Popover.Dropdown>
            <Text size="sm">{t('booking-return-trip-notice')}</Text>
          </Popover.Dropdown>
        </Popover>
      </div>
      <FlightListing
        flights={flightsWithAvailability}
        loading={loading || isFetchingSlots}
        onSelect={handleFlightSelect}
        status={trip.status}
        tripNumber={content.tripNumber}
      />
      <Tooltip
        label={statusTooltip[trip.status]}
        disabled={!checked || !statusTooltip[trip.status]}
        withArrow
        multiline
        maw={280}>
        <Box w="fit-content" data-cy="unknown-trip-end-section">
          <Switch
            data-cy="unknown-trip-end"
            id="unknown-trip-end"
            name="unknown-trip-end"
            label={thumbLabel[trip.status]}
            onChange={handleToggleUnknownTrip}
            checked={checked}
            color={thumbColor[trip.status]}
            thumbIcon={thumbIcon[trip.status]}
          />
        </Box>
      </Tooltip>

      <UnfoundTripNumberModal
        opened={modalOpened}
        onClose={closeModal}
        onConfirmWithTransport={handleConfirmWithTransport}
        onConfirmWithFlightNumber={handleConfirmWithFlightNumber}
        onConfirmNoFlightNumber={handleConfirmNoFlightNumber}
        site={arrivalSite.id}
        date={localDate ?? ''}
      />
    </Stack>
  );
};
